import { Button } from "@aurumiq/ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { FilePlus, LayoutTemplate, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { errorMessage } from "@/lib/api";
import type { CmsPageKind, CmsPageSummary } from "@/lib/types";
import { NewPageDialog } from "./new-page-dialog";
import { useCmsPages, useDeletePage } from "./queries";

const col = createColumnHelper<CmsPageSummary>();

/** Shared list for /cms/pages and /cms/posts: `kinds` limits the filter tabs and the New dialog. */
export function PagesTable({
  title,
  description,
  kinds,
  editBase,
}: {
  title: string;
  description: string;
  kinds: readonly CmsPageKind[];
  editBase: string;
}) {
  const navigate = useNavigate();
  const canWrite = usePermission("cms:write");
  const [sp, setSp] = useSearchParams();
  const kindParam = sp.get("kind") ?? "";
  const kind = (kinds as readonly string[]).includes(kindParam) ? (kindParam as CmsPageKind) : "";
  // /cms/posts has no "all" endpoint filter that excludes other kinds, so fetch all and filter.
  const pages = useCmsPages(kind);
  const del = useDeletePage();
  const [newOpen, setNewOpen] = useState(false);
  const [deleting, setDeleting] = useState<CmsPageSummary | null>(null);

  const columns = useMemo(
    () =>
      [
        col.accessor("title", {
          header: "Title",
          cell: (c) => (
            <Link
              to={`${editBase}/${c.row.original.id}`}
              className="font-semibold text-primary hover:underline"
            >
              {c.getValue()}
            </Link>
          ),
        }),
        col.accessor("slug", {
          header: "Slug",
          cell: (c) => <span className="font-mono text-xs">/{c.getValue()}</span>,
        }),
        col.accessor("kind", { header: "Kind" }),
        col.accessor("status", {
          header: "Status",
          cell: (c) => (
            <StatusTag variant={statusVariant(c.getValue())}>
              {c.getValue().toUpperCase()}
            </StatusTag>
          ),
        }),
        col.accessor("updated_at", {
          header: "Updated",
          cell: (c) => (
            <span title={dmyTime(c.getValue())}>
              {relativeTime(c.getValue())}
              {c.row.original.updated_by ? (
                <span className="text-muted-foreground text-xs">
                  {" "}
                  · {c.row.original.updated_by}
                </span>
              ) : null}
            </span>
          ),
        }),
        col.display({
          id: "actions",
          header: "",
          meta: { numeric: true },
          cell: (c) =>
            canWrite ? (
              <span className="inline-flex items-center gap-1">
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  aria-label={`Design ${c.row.original.title}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link to={`${editBase}/${c.row.original.id}/design`}>
                    <LayoutTemplate /> Design
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive"
                  aria-label={`Delete ${c.row.original.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleting(c.row.original);
                  }}
                >
                  <Trash2 />
                </Button>
              </span>
            ) : null,
        }),
      ] as ColumnDef<CmsPageSummary, unknown>[],
    [canWrite, editBase],
  );

  const rows = (pages.data?.items ?? []).filter((p) =>
    (kinds as readonly string[]).includes(p.kind),
  );

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        actions={
          canWrite ? (
            <Button variant="gold" onClick={() => setNewOpen(true)}>
              <FilePlus /> New {kinds.length === 1 ? kinds[0] : "page"}
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns}
        data={rows}
        isLoading={pages.isPending}
        getRowId={(p) => p.id}
        onRowClick={(row) => navigate(`${editBase}/${row.original.id}`)}
        emptyMessage={
          pages.isError ? `Could not load pages: ${errorMessage(pages.error)}` : "No pages yet."
        }
        toolbar={
          kinds.length > 1 ? (
            <Tabs
              value={kind || "all"}
              onValueChange={(v) => setSp(v === "all" ? {} : { kind: v })}
            >
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                {kinds.map((k) => (
                  <TabsTrigger key={k} value={k}>
                    {k}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ) : null
        }
      />
      <NewPageDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        kinds={kinds}
        defaultKind={kind || kinds[0] || "page"}
        onCreated={(p) => navigate(`${editBase}/${p.id}`)}
      />
      <ReasonDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete “${deleting?.title}”?`}
        description="Removes the page and every revision. The public URL stops resolving immediately."
        confirmLabel="Delete page"
        onConfirm={async (reason) => {
          if (deleting) await del.mutateAsync({ id: deleting.id, reason });
        }}
      />
    </>
  );
}
